import { useEffect, useState, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import TranslatedContent from '../components/TranslatedContent';
import { useLanguage } from '../context/LanguageContext';
import { getNewsByCategory } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Newspaper, ExternalLink, Calendar as CalendarIcon, Globe, Church, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Search, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { stripHtml } from '../utils/textUtils';
import usePageBanner from '../hooks/usePageBanner';
import { AutoTranslate } from '../hooks/useAutoTranslate';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';

const ITEMS_PER_PAGE = 6;

const NewsCard = ({ item, language, t, isRTL }) => {
  const cleanTitle = {
    sv: stripHtml(item.title?.sv || ''),
    en: stripHtml(item.title?.en || item.title?.sv || '')
  };
  
  const cleanExcerpt = {
    sv: stripHtml(item.excerpt?.sv || ''),
    en: stripHtml(item.excerpt?.en || item.excerpt?.sv || '')
  };

  return (
    <Card className="overflow-hidden card-hover border-0 shadow-lg h-full flex flex-col" data-testid={`news-${item.id}`}>
      {item.image_url && (
        <div className="h-48 overflow-hidden">
          <img
            src={item.image_url}
            alt={cleanTitle[language] || cleanTitle['sv']}
            className="w-full h-full object-cover"
          />
        </div>
      )}
      <CardContent className="p-6 flex-1 flex flex-col">
        <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'} text-xs text-gray-500 mb-3`}>
          <CalendarIcon className="w-3 h-3" />
          <span>{new Date(item.published_at).toLocaleDateString('sv-SE')}</span>
          {item.feed_source && (
            <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs">
              {item.feed_source}
            </span>
          )}
        </div>
        <TranslatedContent 
          content={cleanTitle}
          as="h3"
          className="text-lg font-serif font-bold text-gray-900 mb-3 line-clamp-2"
        />
        <TranslatedContent 
          content={cleanExcerpt}
          as="div"
          className="text-sm text-gray-600 line-clamp-3 mb-4 flex-1"
        />
        {item.external_link ? (
          <a href={item.external_link} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm" className="w-full">
              {t('readMore')}
              <ExternalLink className={`${isRTL ? 'mr-2' : 'ml-2'} w-3 h-3`} />
            </Button>
          </a>
        ) : (
          <Button variant="outline" size="sm" className="w-full" disabled>
            {t('readMore')}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

const PaginationControls = ({ currentPage, totalPages, onPageChange, isRTL }) => {
  if (totalPages <= 1) return null;

  return (
    <div className={`flex items-center justify-center gap-2 mt-8 ${isRTL ? 'flex-row-reverse' : ''}`}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(1)}
        disabled={currentPage === 1}
        className="px-3"
        data-testid="pagination-first"
      >
        <ChevronsLeft className="w-4 h-4" />
      </Button>
      
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-3"
        data-testid="pagination-prev"
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>

      <span className="px-4 py-2 text-sm text-gray-600">
        {currentPage} / {totalPages}
      </span>

      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-3"
        data-testid="pagination-next"
      >
        <ChevronRight className="w-4 h-4" />
      </Button>

      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(totalPages)}
        disabled={currentPage === totalPages}
        className="px-3"
        data-testid="pagination-last"
      >
        <ChevronsRight className="w-4 h-4" />
      </Button>
    </div>
  );
};

const DateFilterPopover = ({ news, selectedDate, onDateSelect, onClear, isRTL }) => {
  const [open, setOpen] = useState(false);
  
  // Get all dates that have news
  const datesWithNews = useMemo(() => {
    const dates = new Set();
    news.forEach(item => {
      if (item.published_at) {
        const date = new Date(item.published_at);
        dates.add(date.toISOString().split('T')[0]);
      }
    });
    return dates;
  }, [news]);

  // Custom day render to highlight days with news
  const modifiers = {
    hasNews: (date) => {
      const dateStr = date.toISOString().split('T')[0];
      return datesWithNews.has(dateStr);
    }
  };

  const modifiersStyles = {
    hasNews: {
      backgroundColor: '#fef3c7',
      fontWeight: 'bold',
      borderRadius: '50%'
    }
  };

  const handleSelect = (date) => {
    if (date) {
      const dateStr = date.toISOString().split('T')[0];
      if (datesWithNews.has(dateStr)) {
        onDateSelect(date);
        setOpen(false);
      }
    }
  };

  const handleToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    onDateSelect(today);
    setOpen(false);
  };

  return (
    <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={`text-xs ${selectedDate ? 'bg-yellow-100 border-yellow-400' : ''}`}
            data-testid="date-filter-btn"
          >
            <CalendarIcon className="w-3 h-3 mr-1" />
            {selectedDate 
              ? selectedDate.toLocaleDateString('sv-SE')
              : <AutoTranslate>Sök efter datum</AutoTranslate>
            }
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="p-2 border-b">
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full text-xs"
              onClick={handleToday}
            >
              <AutoTranslate>Idag</AutoTranslate>
            </Button>
          </div>
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleSelect}
            modifiers={modifiers}
            modifiersStyles={modifiersStyles}
            disabled={(date) => {
              const dateStr = date.toISOString().split('T')[0];
              return !datesWithNews.has(dateStr);
            }}
            initialFocus
          />
          <div className="p-2 border-t text-xs text-gray-500 text-center">
            <AutoTranslate>Gula dagar har nyheter</AutoTranslate>
          </div>
        </PopoverContent>
      </Popover>
      
      {selectedDate && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClear}
          className="px-2 h-8"
          data-testid="clear-date-filter"
        >
          <X className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
};

const NewsPage = () => {
  const { t, language, isRTL } = useLanguage();
  const [searchParams] = useSearchParams();
  // Optional deep-link filter — show only one news category when ?category=vatican|catholic_magazines
  const categoryFilter = searchParams.get('category');
  const showVatican = !categoryFilter || categoryFilter === 'vatican';
  const showCatholic = !categoryFilter || categoryFilter === 'catholic_magazines';
  const [vaticanNews, setVaticanNews] = useState([]);
  const [catholicNews, setCatholicNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vaticanPage, setVaticanPage] = useState(1);
  const [catholicPage, setCatholicPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [vaticanDateFilter, setVaticanDateFilter] = useState(null);
  const [catholicDateFilter, setCatholicDateFilter] = useState(null);
  const { bannerImage, bannerTitle, bannerSubtitle } = usePageBanner('news', language);

  useEffect(() => {
    loadNews();
  }, []);

  const loadNews = async () => {
    try {
      const newsData = await getNewsByCategory(100);
      setVaticanNews(newsData.vatican || []);
      setCatholicNews(newsData.catholic_news || []);
    } catch (error) {
      console.error('Error loading news:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter news by search query
  const filterBySearch = (news, query) => {
    if (!query.trim()) return news;
    const lowerQuery = query.toLowerCase();
    return news.filter(item => {
      const title = (item.title?.sv || item.title?.en || '').toLowerCase();
      const excerpt = (item.excerpt?.sv || item.excerpt?.en || '').toLowerCase();
      return title.includes(lowerQuery) || excerpt.includes(lowerQuery);
    });
  };

  // Filter news by date
  const filterByDate = (news, date) => {
    if (!date) return news;
    const targetDate = date.toISOString().split('T')[0];
    return news.filter(item => {
      if (!item.published_at) return false;
      const itemDate = new Date(item.published_at).toISOString().split('T')[0];
      return itemDate === targetDate;
    });
  };

  // Apply filters
  const filteredVaticanNews = useMemo(() => {
    let result = filterBySearch(vaticanNews, searchQuery);
    result = filterByDate(result, vaticanDateFilter);
    return result;
  }, [vaticanNews, searchQuery, vaticanDateFilter]);

  const filteredCatholicNews = useMemo(() => {
    let result = filterBySearch(catholicNews, searchQuery);
    result = filterByDate(result, catholicDateFilter);
    return result;
  }, [catholicNews, searchQuery, catholicDateFilter]);

  // Reset page when filters change
  useEffect(() => {
    setVaticanPage(1);
  }, [searchQuery, vaticanDateFilter]);

  useEffect(() => {
    setCatholicPage(1);
  }, [searchQuery, catholicDateFilter]);

  const hasNoNews =
    (!showVatican || filteredVaticanNews.length === 0) &&
    (!showCatholic || filteredCatholicNews.length === 0);

  // Pagination calculations
  const vaticanTotalPages = Math.ceil(filteredVaticanNews.length / ITEMS_PER_PAGE);
  const catholicTotalPages = Math.ceil(filteredCatholicNews.length / ITEMS_PER_PAGE);

  const paginatedVaticanNews = filteredVaticanNews.slice(
    (vaticanPage - 1) * ITEMS_PER_PAGE,
    vaticanPage * ITEMS_PER_PAGE
  );

  const paginatedCatholicNews = filteredCatholicNews.slice(
    (catholicPage - 1) * ITEMS_PER_PAGE,
    catholicPage * ITEMS_PER_PAGE
  );

  const clearAllFilters = () => {
    setSearchQuery('');
    setVaticanDateFilter(null);
    setCatholicDateFilter(null);
  };

  return (
    <div className="min-h-screen themed-page" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navbar />

      {/* Header */}
      <section className="themed-hero relative h-80 overflow-hidden">
        <img
          src={bannerImage}
          alt="News"
          className="w-full h-full object-cover"
        />
        <div className={`absolute inset-0 bg-gradient-to-r ${isRTL ? 'from-black/40 to-black/70' : 'from-black/70 to-black/40'} flex items-center`}>
          <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full ${isRTL ? 'text-right' : ''}`}>
            <h1 className="responsive-hero-title font-serif font-bold text-white mb-4 fade-in">
              {bannerTitle || <AutoTranslate>Senaste nytt</AutoTranslate>}
            </h1>
            <p className="text-xl text-gray-200 fade-in">
              {bannerSubtitle || <AutoTranslate>Nyheter från församlingen och Vatikanen</AutoTranslate>}
            </p>
          </div>
        </div>
      </section>

      {/* Search Bar */}
      <section className="py-6 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex flex-col sm:flex-row gap-4 items-center ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
            <div className="relative flex-1 w-full sm:max-w-md">
              <Search className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`} />
              <Input
                type="text"
                placeholder={t('searchNews') || 'Sök nyheter...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} h-12 text-base`}
                data-testid="news-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className={`absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 ${isRTL ? 'left-3' : 'right-3'}`}
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
            {(searchQuery || vaticanDateFilter || catholicDateFilter) && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearAllFilters}
                className="text-xs"
              >
                <X className="w-4 h-4 mr-1" />
                <AutoTranslate>Rensa filter</AutoTranslate>
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* News Content */}
      <section className="py-16" data-testid="news-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20">
              <p className="text-gray-600">{t('loading')}</p>
            </div>
          ) : hasNoNews ? (
            <Card className="p-12 text-center border-0 shadow-xl card-hover">
              <Newspaper className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-2xl font-serif font-bold text-gray-900 mb-2">
                {searchQuery || vaticanDateFilter || catholicDateFilter
                  ? <AutoTranslate>Inga nyheter matchar din sökning</AutoTranslate>
                  : <AutoTranslate>Inga nyheter ännu</AutoTranslate>
                }
              </h3>
              <p className="text-gray-600">
                {searchQuery || vaticanDateFilter || catholicDateFilter
                  ? <AutoTranslate>Prova att ändra dina sökkriterier</AutoTranslate>
                  : <AutoTranslate>Kom tillbaka snart för de senaste uppdateringarna!</AutoTranslate>
                }
              </p>
            </Card>
          ) : (
            <div className="space-y-16">
              {/* Vatican News Section */}
              {showVatican && (filteredVaticanNews.length > 0 || vaticanNews.length > 0) && (
                <div>
                  <div className={`flex flex-col sm:flex-row sm:items-center gap-3 mb-8 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                    <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3 flex-row-reverse' : 'space-x-3'}`}>
                      <Globe className="w-8 h-8 text-yellow-600" />
                      <h2 className="text-3xl font-serif font-bold text-gray-900">
                        {t('vaticanNews')}
                      </h2>
                    </div>
                    <DateFilterPopover
                      news={vaticanNews}
                      selectedDate={vaticanDateFilter}
                      onDateSelect={setVaticanDateFilter}
                      onClear={() => setVaticanDateFilter(null)}
                      isRTL={isRTL}
                    />
                  </div>
                  {filteredVaticanNews.length > 0 ? (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {paginatedVaticanNews.map((item) => (
                          <NewsCard key={item.id} item={item} language={language} t={t} isRTL={isRTL} />
                        ))}
                      </div>
                      <PaginationControls
                        currentPage={vaticanPage}
                        totalPages={vaticanTotalPages}
                        onPageChange={setVaticanPage}
                        isRTL={isRTL}
                      />
                    </>
                  ) : (
                    <p className="text-gray-500 text-center py-8">
                      <AutoTranslate>Inga nyheter för valt datum</AutoTranslate>
                    </p>
                  )}
                </div>
              )}

              {/* Signum News Section */}
              {showCatholic && (filteredCatholicNews.length > 0 || catholicNews.length > 0) && (
                <div>
                  <div className={`flex flex-col sm:flex-row sm:items-center gap-3 mb-8 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                    <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3 flex-row-reverse' : 'space-x-3'}`}>
                      <Church className="w-8 h-8 text-blue-600" />
                      <h2 className="text-3xl font-serif font-bold text-gray-900">
                        <AutoTranslate>Signum nyheter</AutoTranslate>
                      </h2>
                    </div>
                    <DateFilterPopover
                      news={catholicNews}
                      selectedDate={catholicDateFilter}
                      onDateSelect={setCatholicDateFilter}
                      onClear={() => setCatholicDateFilter(null)}
                      isRTL={isRTL}
                    />
                  </div>
                  {filteredCatholicNews.length > 0 ? (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {paginatedCatholicNews.map((item) => (
                          <NewsCard key={item.id} item={item} language={language} t={t} isRTL={isRTL} />
                        ))}
                      </div>
                      <PaginationControls
                        currentPage={catholicPage}
                        totalPages={catholicTotalPages}
                        onPageChange={setCatholicPage}
                        isRTL={isRTL}
                      />
                    </>
                  ) : (
                    <p className="text-gray-500 text-center py-8">
                      <AutoTranslate>Inga nyheter för valt datum</AutoTranslate>
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NewsPage;
